import { useState } from 'react';
import OBR from '@owlbear-rodeo/sdk';
import type { AppServices } from '@dcc/rules/app/services.ts';
import type { SheetMutationGate } from '@dcc/rules/app/sheetMutationGate.ts';
import type { SheetsApiStorage } from '../obr/sheetsApiStorage.ts';
import { contentRegistry } from '@dcc/rules/content/dccCoreContent.ts';
import { buildEffectiveCharacter } from '@dcc/rules/app/effectiveCharacter.ts';
import { attackCooldownState, damageForEffective, planAttackFor } from '@dcc/rules/app/attackService.ts';
import { attackChoicesForOwnedWeapons } from '@dcc/rules/app/weaponInventoryService.ts';
import { resolveAttackAction } from '@dcc/rules/app/attackActionService.ts';
import { damageHistoryEntry } from '@dcc/rules/app/historyEntries.ts';
import { addRoll, type CheckHistoryEntry, type RollHistoryEntry } from '@dcc/rules/rules/rollHistory.ts';
import { activeEffects } from '@dcc/rules/rules/encounter.ts';
import type { RollMode } from '@dcc/rules/rules/rollModes.ts';
import type { CheckResult } from '@dcc/rules/rules/checks.ts';
import { newId } from '@dcc/rules/app/ids.ts';
import { dicePlusRequester } from './dicePlusRequester.ts';
import { obtainAdditiveDice, obtainCheckDice, totalMismatchNote } from './diceRolls.ts';
import { damageAllowanceFor, damageOutcomeFor, type GmCall } from './attackPlay.ts';
import { checkAfterReroll, rerollNotation, rerollProblem, rerollWithAiFavor } from './aiFavorReroll.ts';
import { obtainNotationDice } from './diceRolls.ts';
import { deriveStatsFor } from '@dcc/rules/app/derivedStats.ts';
import { isDamageEntry } from '@dcc/rules/rules/rollHistory.ts';
import { VitalsHeader } from './VitalsHeader.tsx';
import { usePlaySheet } from './usePlaySheet.ts';
import { RollBreakdown } from './RollBreakdown.tsx';
import { resolveSwapChoice, swapOptionsFor, type SwapChoice } from '@dcc/rules/rules/weaponSwap.ts';
import { hotlistCatalogue, inventoryCatalogue } from './inventoryHotlist.ts';
import { isSpell, spellCastActionCost } from '@dcc/rules/rules/spellCast.ts';
import { markCombatActions, markCombatEvade } from './combatActionSync.ts';
import { performEvadeRoll } from '@dcc/rules/app/playSessionService.ts';
import { RollModeContextMenu, type RollModeMenuState } from './RollModeContextMenu.tsx';

/**
 * Attacks, in the desktop's two deliberate steps: roll the Attack Check, then
 * roll damage once the GM calls a hit. The GM holds the target's Evade, so the
 * sheet only rolls; a natural 20 or 1 is decided by the dice alone.
 */
export function PlayAttackTab(props: {
  services: AppServices;
  characterId: string;
  refreshToken?: number;
  onSaveError: (error: unknown) => Promise<string>;
  coordinationGate: SheetMutationGate;
  combatStorage: SheetsApiStorage;
  initialSkillId?: string | null;
  initialMode?: RollMode | null;
}) {
  const play = usePlaySheet(props.services, props.characterId, props.onSaveError, props.coordinationGate, props.refreshToken);
  const [skillId, setSkillId] = useState<string | null>(props.initialSkillId ?? null);
  const [mode, setMode] = useState<RollMode>(props.initialMode ?? 'normal');
  const [rollMenu, setRollMenu] = useState<(RollModeMenuState & { kind: 'attack' | 'evade' }) | null>(null);
  const [lastCheck, setLastCheck] = useState<{ skillId: string; check: CheckResult; entry: RollHistoryEntry; aimingRank?: number } | null>(null);
  const [lastDamage, setLastDamage] = useState<RollHistoryEntry | null>(null);
  const [rolling, setRolling] = useState(false);
  const [useAiming, setUseAiming] = useState(false);
  const [advancementDamageEffectId, setAdvancementDamageEffectId] = useState<string | null>(null);
  const [swapChoice, setSwapChoice] = useState<SwapChoice | null>(null);

  if (play.error && !play.records) return <p className="dcc-save-status dcc-save-warn" role="alert">{play.error}</p>;
  if (!play.records) return <p>Loading…</p>;

  const { character, runtime } = play.records;
  const effects = activeEffects(runtime);
  const lookupItem = (id: string) => contentRegistry.getItem(id);
  const effective = buildEffectiveCharacter(character, effects, lookupItem);
  const attacks = attackChoicesForOwnedWeapons(character, contentRegistry);
  const skill = attacks.find((a) => a.id === skillId) ?? null;
  const plan = skill ? planAttackFor(effective, skill, contentRegistry) : null;
  const cooldown = plan ? attackCooldownState(plan, runtime) : null;
  const swapOptions = skill ? swapOptionsFor(character, skill.id, inventoryCatalogue) : [];
  const selectedSwap = resolveSwapChoice(swapOptions, swapChoice);
  const disabled = play.busy || rolling;

  function choose(id: string) {
    setSkillId(id);
    setLastCheck(null);
    setLastDamage(null);
    setSwapChoice(null);
    setUseAiming(false);
    setAdvancementDamageEffectId(null);
  }

  async function rollAttack(rollMode: RollMode = mode) {
    if (!skill) return;
    const trackerCost: 1 | 2 = isSpell(skill) ? spellCastActionCost(character, skill.id, hotlistCatalogue) : 1;
    const saved = await play.run('The Attack could not be saved.', async (records) => {
      const resolve = (roller: Parameters<typeof resolveAttackAction>[0]) => resolveAttackAction(roller, {
        character: records.character,
        session: records.session,
        runtime: records.runtime,
        skill,
        mode: rollMode,
        useAiming,
        advancementDamageEffectId,
        // The GM holds the target's Evade (shown on the GM Console).
        targetEvade: null,
        effects,
        registry: contentRegistry,
        now: new Date().toISOString(),
        newId,
        swapFromHotlistSlot: selectedSwap?.slot ?? null,
      });
      setRolling(true);
      try {
        const dice = await obtainCheckDice((roller) => { const r = resolve(roller); return r.ok ? r.outcome.check : null; }, `Attack: ${skill.name}`, await dicePlusRequester());
        if (!dice.ok) return dice.reason;
        const outcome = resolve(dice.roller);
        if (!outcome.ok) return outcome.reason;
        const entry = outcome.session.rollHistory[0] as RollHistoryEntry;
        const swapNote = outcome.swap
          ? `${contentRegistry.getItem(outcome.swap.incomingItemId)?.name ?? outcome.swap.incomingItemId} drawn from Hotlist slot ${outcome.swap.slot + 1} as part of the Attack.`
          : null;
        const rollNote = totalMismatchNote(dice.diceTotal, outcome.outcome.check.total) ?? dice.note;
        return {
          after: () => {
            setLastCheck({ skillId: skill.id, check: outcome.outcome.check, entry, aimingRank: outcome.aimingRank });
            setLastDamage(null);
            if (outcome.swap) setSwapChoice(null);
          },
          character: outcome.character,
          session: outcome.session,
          runtime: outcome.runtime ?? undefined,
          note: [swapNote, rollNote].filter((value): value is string => Boolean(value)).join(' ') || null,
        };
      } finally {
        setRolling(false);
      }
    });
    if (saved) {
      try {
        const tracked = await markCombatActions(props.combatStorage, props.characterId, trackerCost, 'player-attack');
        if (tracked.outOfActions) await OBR.notification.show('Out Of Actions', 'WARNING');
      } catch (error) {
        await OBR.notification.show(error instanceof Error ? error.message : 'Combat tracker update failed.', 'WARNING');
      }
    }
  }


  async function rollEvade(rollMode: RollMode = mode) {
    const saved = await play.run('The Evade Check could not be saved.', async (records) => {
      const liveEffects = activeEffects(records.runtime);
      const input = (roller: Parameters<typeof performEvadeRoll>[0]) => performEvadeRoll(roller, {
        character: records.character,
        session: records.session,
        mode: rollMode,
        difficulty: null,
        registry: contentRegistry,
        now: new Date().toISOString(),
        newId,
        effects: liveEffects,
        runtime: records.runtime,
      });
      setRolling(true);
      try {
        const dice = await obtainCheckDice((roller) => input(roller).result, 'Evade', await dicePlusRequester());
        if (!dice.ok) return dice.reason;
        const outcome = input(dice.roller);
        return {
          session: outcome.session,
          runtime: outcome.runtime ?? undefined,
          note: totalMismatchNote(dice.diceTotal, outcome.result.total) ?? dice.note,
        };
      } finally {
        setRolling(false);
      }
    });
    if (saved) {
      try {
        const tracked = await markCombatEvade(props.combatStorage, props.characterId);
        if (tracked.outOfActions) await OBR.notification.show('Out Of Actions', 'WARNING');
      } catch (error) {
        await OBR.notification.show(error instanceof Error ? error.message : 'Combat tracker update failed.', 'WARNING');
      }
    }
  }

  function rerollAttack() {
    if (!lastCheck || isDamageEntry(lastCheck.entry)) return;
    const previous = lastCheck;
    const entry = previous.entry as CheckHistoryEntry;
    void play.run('The reroll could not be saved.', async (records) => {
      setRolling(true);
      try {
        const dice = await obtainNotationDice(rerollNotation(entry), entry.dice.map(() => 20), await dicePlusRequester());
        if (!dice.ok) return dice.reason;
        const out = rerollWithAiFavor(records.character, records.session, entry.id, dice.roller, new Date().toISOString());
        if (!out.ok) return out.reason;
        return {
          character: out.character,
          session: out.session,
          after: () => {
            setLastCheck({ ...previous, skillId: previous.skillId, check: checkAfterReroll(previous.check, out.entry), entry: out.entry });
            setLastDamage(null);
          },
          note: totalMismatchNote(dice.diceTotal, out.entry.total) ?? dice.note,
        };
      } finally {
        setRolling(false);
      }
    });
  }

  function rollDamage(call: GmCall) {
    if (!lastCheck) return;
    const attackSkill = contentRegistry.getSkill(lastCheck.skillId);
    if (!attackSkill) return;
    void play.run('The damage roll could not be saved.', async (records) => {
      const current = buildEffectiveCharacter(records.character, effects, lookupItem);
      const attackPlan = planAttackFor(current, attackSkill, contentRegistry);
      const outcome = damageOutcomeFor(lastCheck.check, call);
      if (!attackPlan || !outcome) return 'This Attack cannot deal damage.';
      const roll = (roller: Parameters<typeof damageForEffective>[0]) => damageForEffective(roller, current, attackPlan, outcome, contentRegistry, lastCheck.aimingRank ?? 0);
      setRolling(true);
      try {
        const dice = await obtainAdditiveDice((roller) => roll(roller).total, `Damage: ${attackSkill.name}`, await dicePlusRequester());
        if (!dice.ok) return dice.reason;
        const damage = roll(dice.roller);
        const entry = damageHistoryEntry({ id: newId(), timestamp: new Date().toISOString(), plan: attackPlan, damage, effective: current });
        return {
          after: () => setLastDamage(entry),
          session: { ...records.session, rollHistory: addRoll(records.session.rollHistory, entry) },
          note: totalMismatchNote(dice.diceTotal, damage.total) ?? dice.note,
        };
      } finally {
        setRolling(false);
      }
    });
  }

  const allowance = lastCheck ? damageAllowanceFor(lastCheck.check) : null;

  return (
    <div className="dcc-play">
      <VitalsHeader character={character} derived={deriveStatsFor(effective)} />
      <section className="dcc-creator-card dcc-combat-quick">
        <div className="dcc-sheet-section-head">
          <h2>Combat Checks</h2>
          <div className="dcc-sheet-roll-modes" aria-label="combat roll mode">
            {(['normal', 'advantage', 'disadvantage'] as RollMode[]).map((m) => (
              <button key={m} className={mode === m ? 'dcc-mini-mode is-selected' : 'dcc-mini-mode'} disabled={disabled} onClick={() => setMode(m)}>
                {m === 'normal' ? 'Normal' : m === 'advantage' ? 'Advantage' : 'Disadvantage'}
              </button>
            ))}
          </div>
        </div>
        <button
          disabled={disabled}
          onClick={() => void rollEvade()}
          onContextMenu={(event) => {
            event.preventDefault();
            setRollMenu({ x: event.clientX, y: event.clientY, label: 'Evade Check', kind: 'evade' });
          }}
          title="Right-click to roll straight, with Advantage, or with Disadvantage."
        >{rolling ? 'Rolling…' : 'Evade Check'}</button>
      </section>
      <section className="dcc-creator-card">
        <h2>Attacks</h2>
        {attacks.length === 0 ? (
          <p className="dcc-empty">This crawler has no Attack Skills.</p>
        ) : (
          <div className="dcc-creator-options">
            {attacks.map((a) => (
              <button
                key={a.id}
                className={a.id === skillId ? 'dcc-creator-option is-selected' : 'dcc-creator-option'}
                onClick={() => choose(a.id)}
              >
                <span>{a.name}</span>
                <span className="dcc-creator-meta">Rank {effective.effectiveSkillRank(a.id)}</span>
              </button>
            ))}
          </div>
        )}
      </section>

      {plan && (
        <section className="dcc-creator-card">
          <h2>{plan.skill.name}</h2>
          <p className="dcc-creator-help">To hit: {plan.toHitFormula}</p>
          {plan.skill.kind === 'ranged-attack' && effective.baseSkillRank('aiming') > 0 && <label>
            <input type="checkbox" checked={useAiming} disabled={disabled} onChange={(e) => setUseAiming(e.target.checked)} />
            Apply Aiming: +{effective.effectiveSkillRank('aiming')} to hit with Disadvantage; Aiming damage on a hit. Marks Aiming for advancement.
          </label>}
          {plan.skill.kind === 'melee-attack' && plan.profile.weaponUse === null && <label className="dcc-creator-field">
            Advancement mark for this hand-to-hand attack
            <select disabled={disabled} value={advancementDamageEffectId ?? ''} onChange={(e) => setAdvancementDamageEffectId(e.target.value || null)}>
              <option value="">Mark the Attack Skill</option>
              {character.skills.filter((s) => contentRegistry.getSkill(s.skillId)?.advancement === 'hand-to-hand-choice').map((s) => <option key={s.skillId} value={s.skillId}>Mark {contentRegistry.getSkill(s.skillId)?.name} instead</option>)}
            </select>
            <span className="dcc-creator-help">Choose a Damage Effect only if you actually use it. The GM adjudicates its effect; this choice records its advancement mark. Previous marks from other uses remain.</span>
          </label>}
          <p className="dcc-creator-help">Damage: {plan.damageFormula}</p>
          {plan.manaCost !== null && (
            <p className="dcc-creator-help">Mana cost {plan.manaCost} (you have {character.currentMana}); spent even on a miss.</p>
          )}
          {plan.notes.length > 0 && <ul className="dcc-creator-problems">{plan.notes.map((n) => <li key={n}>{n}</li>)}</ul>}
          {cooldown?.blocked && <p className="dcc-creator-warn">{cooldown.reason}</p>}

          {swapOptions.length > 0 && (
            <label className="dcc-creator-field">
              Weapon for this Attack
              <select
                value={selectedSwap ? `${selectedSwap.slot}:${selectedSwap.itemId}` : ''}
                disabled={disabled}
                onChange={(event) => {
                  const value = event.target.value;
                  if (!value) { setSwapChoice(null); return; }
                  const option = swapOptions.find((candidate) => `${candidate.slot}:${candidate.itemId}` === value);
                  setSwapChoice(option ? { slot: option.slot, itemId: option.itemId } : null);
                }}
              >
                <option value="">Use current weapon / no Hotlist swap</option>
                {swapOptions.map((option) => (
                  <option key={`${option.slot}:${option.itemId}`} value={`${option.slot}:${option.itemId}`}>
                    Draw {option.name} from slot {option.slot + 1}
                  </option>
                ))}
              </select>
              <span className="dcc-creator-help">A Hotlisted weapon may swap in free as part of this Attack. The weapon put away returns to the Hotlist.</span>
            </label>
          )}

          <p className="dcc-creator-help">Your GM has the target's Evade. Roll, then tell the table your total.</p>
          <div className="dcc-creator-nav">
            <button
              disabled={disabled || cooldown?.blocked === true}
              onClick={() => void rollAttack()}
              onContextMenu={(event) => {
                event.preventDefault();
                setRollMenu({ x: event.clientX, y: event.clientY, label: `Attack: ${plan.skill.name}`, kind: 'attack' });
              }}
              title="Right-click to roll straight, with Advantage, or with Disadvantage."
            >{rolling ? 'Rolling…' : 'Roll Attack'}</button>
          </div>
          {play.error && <p className="dcc-save-status dcc-save-warn" role="alert">{play.error}</p>}
        </section>
      )}

      {lastCheck && (
        <section className="dcc-creator-card">
          <RollBreakdown
            entry={lastCheck.entry}
            reroll={lastDamage ? undefined : { problem: rerollProblem(character, lastCheck.entry), aiFavor: character.aiFavor, disabled, onReroll: rerollAttack }}
          />
          {allowance?.kind === 'critical-fail' && <p className="dcc-creator-warn">Natural 1: Critical Fail. No damage.</p>}
          {allowance?.kind === 'critical-hit' && (
            <>
              <p><strong>Natural 20: Critical Hit.</strong> Base damage dice are doubled.</p>
              <div className="dcc-creator-nav"><button disabled={disabled} onClick={() => rollDamage('hit')}>Roll Critical damage</button></div>
            </>
          )}
          {allowance?.kind === 'gm-decides' && (
            <>
              <p className="dcc-creator-help">If your GM says it hit, roll damage. Amazing Success (beat Evade by 10+) adds the Floor.</p>
              <div className="dcc-creator-nav">
                <button disabled={disabled} onClick={() => rollDamage('hit')}>Hit: roll damage</button>
                <button className="dcc-creator-secondary" disabled={disabled} onClick={() => rollDamage('amazing')}>Amazing Success</button>
              </div>
            </>
          )}
          {lastDamage && <RollBreakdown entry={lastDamage} />}
          {play.note && <p className="dcc-creator-help">{play.note}</p>}
        </section>
      )}
      <RollModeContextMenu
        menu={rollMenu}
        onClose={() => setRollMenu(null)}
        onChoose={(chosen) => {
          const target = rollMenu;
          setRollMenu(null);
          if (!target) return;
          if (target.kind === 'attack') void rollAttack(chosen);
          else void rollEvade(chosen);
        }}
      />
    </div>
  );
}
